import { AdvertContext } from "common/context/Adverts";
import { addTypes } from "common/context/Adverts/Adverts";
import TopBanner from "common/Layout/Adverts/TopBanner";
import CenterWrapper from "Components/CenterWrapper";
import VideoPlayer from "Components/VideoPlayer";
import parse from "html-react-parser";
import { useContext } from "react";
import styled from "styled-components";
import { defaultBreakpoints } from "styled-media-query";
import ArticleImage from "../ArticleImage";
import ArticleSharing from "../ArticleSharing";
import DailyMotion from "../components/DailyMotion";
import ImageGallery from "../components/ImageGallery";
import { ArticleContext } from "../Context";
import { articleTypes } from "../Context/ArticleContext";
import About from "./About";
import BreadCrumbs from "./BreadCrumbs";
import {
  AboutInnerWrapper,
  AboutWrapper,
  CommentsButton,
  DailyMotionWrapper,
  HeaderWrapper,
  Headline,
  SharingWrapper,
} from "./Header.styled";
import SponsorImage from "./SponsorImage";

interface HeaderProps {
  commentCount: number | undefined;
}

const SkeletonBox = styled.div`
  width: 112px;
  height: 24px;
  border-radius: 24px;
  background: ${({ theme }) => `
    linear-gradient(
      90deg,
      ${theme.default.color.primary} 0%,
      ${theme.default.color.primary} 40%,
      rgba(255,255,255,0.3) 50%,
      ${theme.default.color.primary} 60%,
      ${theme.default.color.primary} 100%
    )
  `};
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  display: inline-block;

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AboutRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  @media (max-width: ${defaultBreakpoints.medium}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const Header = ({ commentCount }: HeaderProps) => {
  const { leaderboard } = useContext<addTypes>(AdvertContext);
  const { refactoredArticle, isHeroOverride } =
    useContext<articleTypes>(ArticleContext);
  const {
    title,
    type,
    isYoutube,
    lead,
    hero,
    isVideo,
    dailyMotion,
    hierarchy,
    showComments,
  } = refactoredArticle;

  const isLoadingComments = showComments && typeof commentCount !== "number";
  const canShowButton = showComments && typeof commentCount === "number";

  return (
    <HeaderWrapper
      gallery={hero?.data.bundle === "gallery"}
      isVideo={isVideo || dailyMotion}
    >
      <CenterWrapper>
        {leaderboard && <TopBanner type={type} />}
        <br />
        <BreadCrumbs hierarchy={hierarchy} />
        <Headline>{title}</Headline>
        <div style={{ textAlign: "left", marginBottom: "16px" }}>
          {lead && parse(lead)}
        </div>

        {type !== "Service" && (
          <>
            <AboutWrapper>
              <AboutRow>
                <AboutInnerWrapper>
                  <About />
                </AboutInnerWrapper>
                <SponsorImage type="desktop" />
                {canShowButton && (
                  <CommentsButton
                    className="comments-desktop"
                    as="a"
                    href="#comments"
                  >
                    {commentCount > 0
                      ? `View ${commentCount} comment${
                          commentCount > 1 ? "s" : ""
                        }`
                      : "Leave a comment"}
                  </CommentsButton>
                )}
                {isLoadingComments && (
                  <CommentsButton
                    className="comments-desktop"
                    as="a"
                    href="#comments"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <SkeletonBox />
                  </CommentsButton>
                )}
                <SharingWrapper>
                  <ArticleSharing />
                </SharingWrapper>
              </AboutRow>
            </AboutWrapper>
            <SponsorImage type="mobile" />
            {canShowButton && (
              <CommentsButton
                className="comments-mobile"
                as="a"
                href="#comments"
              >
                {commentCount > 0
                  ? `View ${commentCount} comment${commentCount > 1 ? "s" : ""}`
                  : "Leave a comment"}
              </CommentsButton>
            )}
            {isLoadingComments && (
              <CommentsButton
                className="comments-mobile"
                as="a"
                href="#comments"
                tabIndex={-1}
                aria-hidden="true"
              >
                <SkeletonBox />
              </CommentsButton>
            )}
          </>
        )}
        {type === "Commercial" && (
          <>
            {hero?.type === "dailymotion" && (
              <DailyMotionWrapper>
                <DailyMotion isHero={true} data={hero.data} />
              </DailyMotionWrapper>
            )}
            {hero?.type === "image" && (
              <ArticleImage type="commerical" image={hero.data} />
            )}
          </>
        )}
        {isVideo && <VideoPlayer hero={hero} />}
        {dailyMotion && (
          <DailyMotionWrapper>
            <DailyMotion data={hero.data} isHero={true} />
          </DailyMotionWrapper>
        )}
        {hero?.data.bundle === "gallery" && (
          <ImageGallery hero={true} data={hero.data} />
        )}
      </CenterWrapper>
    </HeaderWrapper>
  );
};

export default Header;
